import { useCallback } from 'react';
import { isNil } from 'lodash';
import styled from 'styled-components';

// COMPONENTS
import ConnectedField from './connectedField';

// HOCS
import withFormikInput from './withFormikInput';

// UTILS
import { isNilOrEmpty } from 'utils';
import { NumberField } from './numberField';

// ASSETS
import BatteryIcon from 'assets/BatteryIcon';

const config = {
  getValidity: ({ connectedBatteryLevel }) => !isNil(connectedBatteryLevel),
  getConnectivityStatusText: (meta) => {
    if (isNil(meta.connectedBatteryLevel)) {
      return { translationId: 'services.Vehicles.batteryLevelNotAvailable' };
    }

    return null;
  },
};

const InputContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const StyledBatteryIcon = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
`;

const StyledNumberField = styled(NumberField)`
  width: 100%;

  &&& input {
    padding-left: 38px;
  }
`;

const BatteryNumberInput = (props) => {
  const { isCharging, ...rest } = props;
  const getBatteryIconSize = (desiredHeight: number, originalWidth = 32, originalHeight = 24) => {
    const aspectRatio = originalWidth / originalHeight;
    return {
      height: desiredHeight,
      width: Math.round(desiredHeight * aspectRatio),
    };
  };
  const batteryIconProps = {
    ...getBatteryIconSize(16),
    isCharging: isCharging,
    batteryLevel: props.value,
  };

  return (
    <InputContainer>
      <StyledBatteryIcon>
        <BatteryIcon {...batteryIconProps} />
      </StyledBatteryIcon>
      <StyledNumberField {...rest} />
    </InputContainer>
  );
};

const _ConnectedBatteryField = (props) => {
  const {
    connectedMetadata = {},
    disabled = false,
    readOnly = false,
    touched = false,
    edited,
    error,
    label,
    name,
    onChange,
    onClickRefresh,
    required,
    setEdited,
    value,
    validators,
    setAutofillEditedValues,
    ...rest
  } = props;

  // Event handler: onChange
  const handleChange = useCallback(
    (event) => {
      if (event.persist) event.persist();

      const { value: newValue } = event.target;

      if (newValue !== value || !edited) {
        onChange && onChange(event);
      }

      if (!edited) {
        setEdited && setEdited(name, true);
      }

      // if (setAutofillEditedValues) {
      //   setAutofillEditedValues(name, newValue);
      // }
    },
    [onChange, value, edited, setEdited, name]
  );

  // Event handler: refresh
  const handleClickRefresh = useCallback(() => {
    onClickRefresh && onClickRefresh(name, 'batteryLevel');
  }, [onClickRefresh, name]);

  // Compute connected battery value and charge state
  const connectedBatteryLevel = connectedMetadata.isConnected ? connectedMetadata.connectedBatteryLevel : '';
  const isCharging = connectedMetadata.isConnected ? connectedMetadata.connectedChargeState : false;

  // resolves the value to show
  const resolvedValue = !isNilOrEmpty(value) || edited ? value : connectedBatteryLevel;

  // Field input props
  const fieldProps = {
    ...rest,
    disabled,
    name,
    readOnly,
    onChange: handleChange,
    value: resolvedValue,
    validators,
    isCharging: isCharging,
  };

  // ConnectedField container props
  const connectedFieldProps = {
    name,
    label,
    readOnly,
    error,
    disabled,
    hasValue: !isNilOrEmpty(resolvedValue),
    isConnected: connectedMetadata.isConnected,
    isManuallyEdited: !isNilOrEmpty(value) || edited,
    isValid: config.getValidity(connectedMetadata),
    lastConnectedDate: connectedMetadata.connectedBatteryLevelDate,
    onClickRefresh: handleClickRefresh,
    statusText: config.getConnectivityStatusText(connectedMetadata),
  };

  return (
    <ConnectedField {...connectedFieldProps}>
      <BatteryNumberInput {...fieldProps} />
    </ConnectedField>
  );
};

const FormConnectedBatteryField = withFormikInput(_ConnectedBatteryField, {
  passProps: ['touched'],
  watchedProps: ['edited', 'connectedMetadata'],
});

export default FormConnectedBatteryField;
